import type { HTMLAttributes, KeyboardEventHandler } from 'react';

import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import ProductLine from 'components/ProductLine';
import Text from 'components/Text';
import type { ProductCard } from 'models/productCard';
import { is, range } from 'utils/helpers';

interface Props extends HTMLAttributes<HTMLDivElement> {
	/** Heading text. */
	heading?: string;

	/** Number of products to fetch. */
	itemLimit?: number;

	/** Tab key handler for the last term link. */
	onLastLinkTab?: KeyboardEventHandler<HTMLAnchorElement>;

	products: ProductCard[] | undefined;
}

export default function PopularProductsList({
	heading,
	itemLimit,
	onLastLinkTab,
	products,
	...props
}: Props) {
	const hasProducts = is.arrayWithLength(products);
	const itemMarginClasses = 'first:mt-0 mt-6 md:mt-4';

	const handleLastLinkKeyDown: KeyboardEventHandler<HTMLAnchorElement> = (
		e,
	) => {
		if (onLastLinkTab && e.key === 'Tab' && !e.shiftKey) {
			onLastLinkTab(e);
		}
	};

	return (
		<div {...props}>
			{heading && (
				<Text as="h3" className="mb-6">
					{heading}
				</Text>
			)}
			{hasProducts && (
				<ul>
					{products.map((product, i) => (
						<li key={product.id} className={itemMarginClasses}>
							<ProductLine
								product={product}
								onKeyDown={
									i === products.length - 1 ? handleLastLinkKeyDown : undefined
								}
							/>
						</li>
					))}
				</ul>
			)}
			{!hasProducts && (
				<LoadingSkeleton>
					{/* Don't want zero. */}
					{/* oxlint-disable-next-line typescript/prefer-nullish-coalescing */}
					{range(itemLimit || 5).map((i) => (
						<LoadingSkeletonItem
							key={i}
							width="100%"
							height="40px"
							className={itemMarginClasses}
						/>
					))}
				</LoadingSkeleton>
			)}
		</div>
	);
}
PopularProductsList.displayName = 'PopularProductsList';
